import { useState } from "react";
import { Expand } from "lucide-react";
import { useLang } from "@/i18n";
import { IMG } from "@/data/media";
import { PageHero } from "@/components/PageHero";
import { FadeUp } from "@/components/motion";
import { Lightbox } from "@/components/Lightbox";
import { SupportCta } from "@/components/SupportCta";
import { BackLink, Objectives, ProjectGallery, StatusNote } from "@/components/ProjectParts";

const FloorPlans = () => {
  const { t } = useLang();
  const v = t.projects.villa;
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(null);
  const items = v.floors.map(([, , k]) => ({ src: IMG[k], caption: t.gallery.captions[k] }));
  const [title, text, key] = v.floors[active];
  return (
    <section className="py-20 sm:py-28 bg-white" data-testid="villa-floorplans">
      <div className="wrap">
        <h2 className="text-4xl sm:text-5xl">{v.floorsTitle}</h2>
        <div className="mt-8 flex flex-wrap gap-2" role="tablist">
          {v.floors.map(([name], i) => (
            <button key={name} role="tab" aria-selected={i === active} onClick={() => setActive(i)} data-testid={`floor-tab-${i}`} className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 ${i === active ? "bg-[var(--green-deep)] text-white" : "bg-[var(--green-pale)] text-[var(--green-deep)] hover:bg-[var(--yellow-pale)]"}`}>{name}</button>
          ))}
        </div>
        <div className="mt-10 grid gap-10 lg:grid-cols-12 items-start">
          <div className="lg:col-span-7">
            <button onClick={() => setZoom(active)} aria-label={`${t.gallery.open}: ${items[active].caption}`} className="group relative block w-full rounded-[2rem] overflow-hidden bg-[var(--cream)] shadow-[0_0_0_1px_var(--line)] p-4 sm:p-8" data-testid="floor-plan-image-btn">
              <img key={key} src={IMG[key]} alt={items[active].caption} className="w-full h-auto max-h-[640px] object-contain mx-auto mix-blend-multiply" data-testid="floor-plan-image" />
              <span className="absolute right-4 bottom-4 h-11 w-11 grid place-items-center rounded-full bg-[var(--yellow)]"><Expand size={17} /></span>
            </button>
          </div>
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <p className="font-display italic text-6xl text-[var(--green-deep)]">0{active + 1}</p>
            <h3 className="mt-2 text-3xl sm:text-4xl" data-testid="floor-plan-title">{title}</h3>
            <p className="mt-5 text-lg leading-relaxed text-[var(--ink-soft)]">{text}</p>
            <p className="mt-6 text-xs uppercase tracking-[0.18em] text-[var(--ink-soft)]">{t.common.source} · IMADE®</p>
          </div>
        </div>
      </div>
      <Lightbox items={items} index={zoom} onClose={() => setZoom(null)} onChange={setZoom} />
    </section>
  );
};

const Renders = () => {
  const { t } = useLang();
  const v = t.projects.villa;
  const [zoom, setZoom] = useState(null);
  const items = ["render3d", "agrinido", "elevations", "aerial"].map((k) => ({ src: IMG[k], caption: t.gallery.captions[k] }));
  const Btn = ({ i, className, contain = false }) => (
    <button onClick={() => setZoom(i)} aria-label={`${t.gallery.open}: ${items[i].caption}`} className={`group relative block w-full overflow-hidden img-zoom bg-white ${className}`} data-testid={`villa-render-${i}`}>
      <img src={items[i].src} alt={items[i].caption} loading="lazy" className={`w-full h-full ${contain ? "object-contain p-6 pb-14" : "object-cover"}`} />
      <span className="absolute left-4 bottom-4 px-3 py-1.5 rounded-full bg-white/90 text-xs font-semibold">{items[i].caption}</span>
    </button>
  );
  return (
    <section className="py-20 sm:py-28" data-testid="villa-renders">
      <div className="wrap">
        <div className="grid gap-6 lg:grid-cols-12 items-end">
          <h2 className="lg:col-span-6 text-4xl sm:text-5xl">{v.renderTitle}</h2>
          <p className="lg:col-span-6 text-lg leading-relaxed text-[var(--ink-soft)]">{v.renderText}</p>
        </div>
        <div className="mt-12 grid gap-4 lg:grid-cols-12">
          <FadeUp className="lg:col-span-12"><Btn i={0} className="aspect-[16/10] sm:aspect-[16/9] rounded-[2.5rem_2.5rem_2.5rem_0.5rem]" /></FadeUp>
          <FadeUp className="lg:col-span-7"><Btn i={1} className="aspect-[16/10] rounded-[2rem]" /></FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-5"><Btn i={2} contain className="aspect-[16/10] lg:aspect-auto lg:h-full rounded-[2rem] shadow-[0_0_0_1px_var(--line)]" /></FadeUp>
        </div>
        <div className="mt-20 grid gap-10 lg:grid-cols-12 items-center" data-testid="villa-aerial">
          <FadeUp className="lg:col-span-5"><Btn i={3} className="aspect-square petal-bl" /></FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-6 lg:col-start-7">
            <h2 className="text-4xl sm:text-5xl">{v.aerialTitle}</h2>
            <p className="mt-5 text-lg leading-relaxed text-[var(--ink-soft)]">{v.aerialText}</p>
          </FadeUp>
        </div>
      </div>
      <Lightbox items={items} index={zoom} onClose={() => setZoom(null)} onChange={setZoom} />
    </section>
  );
};

export default function Villa() {
  const { t } = useLang();
  const v = t.projects.villa;
  return (
    <>
      <PageHero eyebrow={v.place} lines={["Villa Bevilacqua", "Relais"]} intro={v.intro} image={IMG.render3d} imageAlt={t.gallery.captions.render3d} corner="tr" accent="var(--yellow)">
        <div className="mt-8"><BackLink /></div>
      </PageHero>
      <Objectives items={v.objectives} />
      <Renders />
      <FloorPlans />
      <div className="pt-16"><StatusNote /></div>
      <ProjectGallery keys={["aerial", "render3d", "agrinido", "elevations", "planSB", "planGF", "planFF", "planSF"]} />
      <SupportCta title={t.common.supportProject} text={t.common.supportProjectText} />
    </>
  );
}
